import { EventList, EventListContent } from '@/components/event-list';
import { ConferenceContext, RoomContext } from '@/store';
import { findCurrentDayId } from '@/lib/utils/DataFormat';
import { IEvent } from '@/types/event';
import { INode, INodeProps } from '@/types/node';
import { IProgramRoom } from '@/types/room';
import { Box, Container } from '@mui/material';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { IDay } from '@/types/day';
import { IContent } from './contract';
import { useNodeProps } from '@/node-editor/hooks';
import { favoriteEvents } from '@shared/utils/dataReducers';
import { useTheme, Theme } from '@mui/material/styles';

interface INodeExtended extends Omit<INode, 'content'> {
  content: IContent;
}

interface IProps extends Omit<INodeProps, 'node'> {
  node: INodeExtended;
}

const useStyles = (
  theme: Theme,
  {
    override,
  }: Omit<IContent, 'defaultDisplay'> & { override: Record<string, any> }
) => ({
  root: {
    overflow: 'hidden !important',
    ...override.root,
  },
  gridContainer: {
    justifyContent: 'space-between !important',
    marginBottom: `${theme.spacing(1)} !important`,
  },
  forumLink: {
    color: `${theme.palette.grey[500]} !important`,
  },
});

export const Render = ({
  node: {
    id,
    override,
    content: { defaultDisplay },
  },
}: IProps) => {
  const theme = useTheme();
  const classes = useStyles(theme, { override });
  const {
    conferenceState: {
      conferenceDetail,
      conferenceDetail: { programDays: days },
    },
  } = useContext(ConferenceContext);

  const { roomState } = useContext(RoomContext);

  const findCurrentRoom = (conferenceData, roomData) => {
    const activeCurrentDay = conferenceData.programDays.find(
      (day) => day.id === roomData.programDay.id
    );
    let currentProgramRoom = activeCurrentDay.programRooms.find(
      (programroom) => programroom.id === roomData.id
    );
    return currentProgramRoom;
  };

  const [currentProgramRoom, setCurrentProgramRoom] = useState(
    findCurrentRoom(conferenceDetail, roomState.room)
  );

  const [currentTab, setCurrentTab] = useState(
    findCurrentDayId(conferenceDetail)
  );

  const currentDay = useMemo((): IDay => {
    return days.find((d: IDay) => d.id === currentTab);
  }, [currentTab]);
  const totalFavorites = favoriteEvents(conferenceDetail, currentTab);

  useEffect(() => {
    setCurrentProgramRoom(findCurrentRoom(conferenceDetail, roomState.room));
  }, [totalFavorites.length]);

  if (!conferenceDetail || days.length === 0) {
    console.log('No active days, so dont render the program');
    return null; // Don't render with 0 active days
  }

  // Display program as list or grid?
  const defaultDisplayType = () =>
    defaultDisplay === 'grid' ||
    (defaultDisplay === 'auto' && currentDay.programRooms.length > 1);
  const [displayGrid, setDisplayGrid] = useState(defaultDisplayType());

  useEffect(() => {
    setDisplayGrid(defaultDisplayType());
  }, [currentDay.id]);

  const eventStepper = currentDay.programRooms.reduce(
    (acc, { id, currentEventId, video_state }) => ({
      ...acc,
      current: [...acc.current, currentEventId].filter(Boolean),
      finished: [...acc.finished, video_state === 'file' ? id : null].filter(
        Boolean
      ),
    }),
    { current: [], finished: [] }
  );

  const programReducer = (rooms: IProgramRoom[]): IEvent[][] => {
    const eventMap = new Map();

    rooms
      .reduce((acc, curr) => [...acc, ...curr.events], [])
      .forEach((event) => {
        const prev = eventMap.get(event.start_time);

        return !prev
          ? eventMap.set(event.start_time, [event])
          : prev.push(event);
      });

    const sorted = new Map([...eventMap.entries()].sort());

    return [...sorted.values()];
  };

  // const autoGrid = () =>
  //   defaultDisplay === 'auto' && currentDay.programRooms.length > 1;

  return (
    <Container
      maxWidth={false}
      classes={{ root: classes.root }}
      {...useNodeProps(id)}
    >
      <Box maxWidth={800} style={{ width: '100%' }}>
        {currentProgramRoom && (
          <EventList
            rooms={[currentProgramRoom]}
            list={programReducer([currentProgramRoom])}
            stepper={eventStepper}
          >
            {({ event, ...provided }) => (
              <EventListContent
                event={event}
                nrOfRooms={currentDay.programRooms.length}
                hideProgramSubHeader={true}
                hideFavoriteButton={true}
                {...provided}
              ></EventListContent>
            )}
          </EventList>
        )}
      </Box>
    </Container>
  );
};
